<template>
  <section class="mx-3 lg:mx-0 max-w-screen-2xl">
    <div class="mt-10 lg:w-3/5 mx-auto">
      <div class="flex flex-col gap-10">
        <div class="flex items-center justify-between">
          <p class="text-lg text-root-2 font-extrabold" id="settingsTourItem2">
            IP WHITELISTING
          </p>
          <div
            @click="viewIPAddresses"
            class="px-10 cursor-pointer bg-accent-subtle rounded-lg text-primary lg:text-sm text-xs py-4 font-normal flex items-center justify-center hover-animation-subtle"
          >
            View IP Address Added
          </div>
        </div>
        <div class="border-main rounded-lg">
          <div v-if="isLoading">
            <api-keys-loader />
          </div>
          <div v-else>
            <div class="lg:px-8 px-3 py-6 flex flex-col gap-5">
              <div class="flex gap-1 flex-col">
                <p class="font-normal text-sm text-black-10">IP WHITELISTING</p>
                <div
                  class="border border-gray-130 h-14 rounded-md w-full flex items-center gap-1 overflow-auto px-2"
                >
                  <el-tag
                    v-for="(tag, index) in ipAddresses"
                    :key="index"
                    closable
                    size="large"
                    :disable-transitions="false"
                    :type="errors[index] ? 'info' : 'danger'"
                    @close="handleClose(index)"
                  >
                    {{ tag }}
                  </el-tag>
                  <input
                    v-model.trim="inputIp"
                    class="h-full flex-1 focus:outline-none"
                    @keyup.enter="handleInputConfirm"
                    @blur="handleInputConfirm"
                  />
                </div>
              </div>
              <div
                v-if="hasErrors || isMaxIpAddress"
                class="flex gap-4 items-center"
              >
                <div>
                  <warning-icon-2 size="h-5 w-5" color="#D71841" />
                </div>
                <p class="font-normal text-error-20 md:text-base text-sm">
                  {{
                    isMaxIpAddress
                      ? 'IP Addresses cannot exceed 10'
                      : 'Invalid IP address format detected'
                  }}
                </p>
              </div>
              <div class="text-gray-muted flex flex-col gap-2">
                <h3>Tips</h3>
                <ul
                  class="list-disc list-inside font-normal md:text-base text-sm leading-relaxed"
                >
                  <li v-for="(tip, index) in tips" :key="index">{{ tip }}</li>
                </ul>
              </div>
              <el-tooltip
                class="item"
                effect="light"
                :disabled="canManageIPAddresses"
                :content="'No access to add IP Addresses'"
                placement="bottom"
              >
                <div
                  v-on="
                    hasErrors ||
                    errors.length < 1 ||
                    isMaxIpAddress ||
                    !canManageIPAddresses
                      ? {}
                      : { click: () => verifyAuth('whitelistIPAddress') }
                  "
                  class="px-10 cursor-pointer bg-primary rounded-lg text-white lg:text-sm text-xs py-4 font-normal flex items-center justify-center hover-animation-subtle w-fit"
                  :class="[
                    {
                      't-opacity-50 cursor-not-allowed bg-secondary':
                        !canManageIPAddresses
                    },
                    hasErrors || errors.length < 1 || isMaxIpAddress
                      ? 'cursor-not-allowed bg-secondary'
                      : null
                  ]"
                >
                  Add IP Addresses
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="authState"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeAuthDialog"
      @whitelistIPAddress="whitelistIPAddress"
    />
  </section>
</template>
<script>
import { IsValidIP } from '@/utils/helpers.js'
import getApiCredentials from '@/mixins/externalApi.js'
import userKinds from '@/mixins/userKinds.js'

export default {
  name: 'IPWhitelisting',
  emits: ['viewIpAddresses'],
  mixins: [getApiCredentials, userKinds],
  components: {
    WarningIcon2: () => import('@/assets/icons/WarningIcon2.vue'),
    VerifyAuthDialog: () => import('@/components/v2/auth/VerifyAuthDialog'),
    ApiKeysLoader: () =>
      import('@/components/v2/skeletalLoaders/ApiKeysLoader.vue')
  },
  data () {
    return {
      isLoading: false,
      activeIPTab: 'create',
      tips: [
        'Enter the IP address of the servers you want to whitelist (You can whitelist a maximum of 10 IP addresses).',
        'Ensure the IP address is in a valid format (IPv4 or IPv6)',
        'To add more than one IP address, enter an IP range or separate each IP address with a comma.',
        'It can take up to an hour for any changes to take effect.',
        'Note: Be sure to enter public IP addresses, as private IP addresses are not accepted.'
      ],
      ipAddresses: [],
      inputIp: '',
      errors: [],
      showVerifyAuthDialog: false,
      authState: '',
      maxIpAddress: 10
    }
  },
  methods: {
    viewIPAddresses () {
      this.$emit('view')
    },
    handleInputConfirm () {
      if (this.inputIp) {
        this.ipAddresses.push(this.inputIp)
        this.errors.push(IsValidIP(this.inputIp))
      }
      this.inputIp = ''
    },
    handleClose (index) {
      this.ipAddresses.splice(index, 1)
      this.errors.splice(index, 1)
    },
    verifyAuth (authState) {
      this.showVerifyAuthDialog = true
      this.authState = authState
    },
    closeAuthDialog () {
      this.showVerifyAuthDialog = false
    },
    whitelistIPAddress (twoFaCode) {
      const payload = {
        ipAddress: this.ipAddresses,
        twoFaCode: twoFaCode.code
      }
      this.$store
        .dispatch('externalApi/whitelistIPs', { ...payload })
        .then(async () => {
          this.showVerifyAuthDialog = false
          this.ipAddresses = []
          this.errors = []
          await this.getApiCredentials()
          this.$notify({
            title: 'Success',
            message: 'IP whitelisted successfully',
            type: 'success'
          })
        })
        .catch(() => {
          this.showVerifyAuthDialog = false
        })
        .finally(() => {
          this.showVerifyAuthDialog = false
        })
    }
  },
  computed: {
    hasErrors () {
      return this.errors.length > 0 && !this.errors.every((err) => err)
    },
    isMaxIpAddress () {
      return this.ipAddresses.length > this.maxIpAddress
    }
  }
}
</script>
